import { LazyQueryHookOptions } from "@apollo/client"
import Codenames from "constants/codenames"
import ROUTES from "constants/routes.constants"
import dayjs from "dayjs"
import {
  AddDcMutationVariables,
  AllDcLocationsQuery,
  DistributionCentersDetailQuery,
  Exact,
  useAddDcMutation,
  useAllBanksSelectorQuery,
  useAllDcLocationsLazyQuery,
  useAllPeopleSelectorQuery,
  useDistributionCentersDetailLazyQuery,
  useDistributionCentersListQuery,
  useUpdateDcMutation,
} from "generated/graphql"
import { useSearchFilter } from "hooks/searchFilterHooks"
import { isEmpty, isNil, isPlainObject, omit, omitBy, pick } from "lodash-es"
import useDCStore, { DCStoreType } from "pages/registration/dc/dc.store"
import qs from "query-string"
import { useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { useFilter } from "store/search-filter/search-filter.reducer"
import AppToaster, { ErrorToast } from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import { readableHour } from "utils/date-utils"
import { jsonToGis } from "utils/map.utils"

export const useDistributionCentersList = (isCpc = false) => {
  const [{ search, page }] = useSearchFilter()
  const defaultFilter = useMemo(() => ({}), [])
  const [filter] = useFilter("distribution-centers")
  return useDistributionCentersListQuery({
    variables: {
      query: search ?? "",
      first: 13,
      skip: page * 13,
      filter: isCpc
        ? {
            isManufacturingPlant: isCpc,
            department: filter?.department?.id,
            ...defaultFilter,
          }
        : { department: filter?.department?.id, ...defaultFilter },
    },
  })
}

export const usePeopleList = (query: string, group: string = "*") => {
  return useAllPeopleSelectorQuery({
    variables: {
      query,
      first: 10,
      group: group,
    },
  })
}

export const useDistributionCenterDetails = (
  onCompleted: ((data: DistributionCentersDetailQuery) => void) | undefined
) => {
  return useDistributionCentersDetailLazyQuery({ onCompleted })
}

export const useAllDcLocations = (
  options?: LazyQueryHookOptions<
    AllDcLocationsQuery,
    Exact<{
      pocId?: string
    }>
  >
) => useAllDcLocationsLazyQuery(options)

const mapSlots = (slot: any, plantCode: any, type: any) => {
  const d = slot?.slots
  return {
    id: slot?.id,
    fromTime: d?.[0] ? `${d[0]}:00` : undefined,
    toTime: d?.[1] ? (d[1] === 24 ? "23:59:59" : `${d[1]}:00`) : undefined,
    name: d
      ?.map?.(h => readableHour(h))
      ?.join("-")
      .concat(` ${plantCode}`),
    maximumOrders: slot.maximumOrders ?? undefined,
    thresholdFrom: slot.thresholdFrom
      ? dayjs(slot.thresholdFrom).format("HH:mm:ss")
      : undefined,
    estimatedDelivery: slot.estimatedDelivery?.id || undefined,
  }
}

export const useAddUpdateDistributionCentre = (
  onCompleted?: (data: any) => void
) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { data: bData, loading: bLoading } = useAllBanksSelectorQuery()

  const allBanks = bData?.allBanks
  const upi = useMemo(
    () => allBanks?.find(bank => bank.codename === Codenames.UPI),
    [allBanks]
  )
  const search = qs.parse(location.search)
  const [addMutation, { loading: addLoading, data }] = useAddDcMutation({
    onCompleted(data) {
      const dc = data.addDistributionCentre.distributionCentre
      AppToaster.show({
        message: `${dc.properties.name} created successfully`,
        intent: "success",
        icon: "plus",
      })
      navigate(ROUTES.dcDetail(dc.id), { replace: true })
      onCompleted?.(dc)
    },
    onError(error) {
      ErrorToast(error?.message || "Unable to add the distribution center")
    },
  })
  const [updateMutation, { loading: updateLoading }] = useUpdateDcMutation({
    onCompleted(data) {
      const dc = data.updateDistributionCentre.distributionCentre
      AppToaster.show({
        message: `${dc.properties.name} updated successfully`,
        intent: "success",
        icon: "tick-circle",
      })
      onCompleted?.(dc)
    },
    onError() {
      ErrorToast("Unable to update the distribution center")
    },
  })
  function addUpdate(resetState: boolean = false) {
    const store: DCStoreType = useDCStore.getState()

    const changed = store.changed
    const details = changed?.basicDetails
    const deliveryConditions = changed?.deliveryCharges
    const billingDetails = changed?.billingDetails
    const gst = omit(billingDetails, [
      "businessPlace",
      "profitCenter",
      "costCenter",
    ])

    // return null
    const slots = changed?.operationalSlots
    for (const error in store.errors) {
      if (Object.prototype.hasOwnProperty.call(store.errors, error)) {
        const module = store.errors[error]
        if (module.total) {
          return AppToaster.show({
            message: module.latest,
            icon: "cross",
            intent: "danger",
          })
        }
      }
    }

    const baseVariables: AddDcMutationVariables = {
      ...pick(details, ["companyCode", "salesOfcCode", "partnerCode", "name"]),
      customerCode: details?.customerCode as any,
      plantCode: details?.plantCode as any,
      storeCode: details?.storeCode as any,
      colorCode: details?.colorCode?.id,
      primaryPocId:
        details?.primaryPoc?.id === null ? "NULL" : details?.primaryPoc?.id,
      assignedCpc:
        search.type === "cpc"
          ? null
          : details?.assignedCpc?.id === null
          ? null
          : details?.assignedCpc?.id,
      groupVisibility: details?.groupVisibility?.map?.(g => g.id),
      address: changed.basicDetails?.address
        ? omit(
            {
              ...changed.basicDetails?.address,
              geofenceRadius: 10000,
            },
            ["__typename"]
          )
        : undefined,
      deliveryConditions: {},
      pointOfContacts: details?.pointOfContacts?.map(p => p.id),
      allowedDepartments: details?.allowedDepartments?.map(p => p.id),
      photos: details?.photos?.map(p => ({
        id: p?.id,
        mediaType: "PHT",
        url: p?.url,
      })),
      areaCatered: Array.isArray(details?.geometry?.coordinates)
        ? JSON.stringify({
            // This is a workaround to get the coordinates in the correct format
            // This works in case `coordinates` is an array of arrays or array of objects
            coordinates: isPlainObject(details?.geometry?.coordinates)
              ? Object.values(details?.geometry?.coordinates)
              : details?.geometry?.coordinates,
            type: "MultiPolygon",
          })
        : undefined,
    }
    if (store.state.basicDetails?.id) {
      const currentSP = changed?.stockPreferences?.currentStockPercentage
      const extraSapCodes = omitBy(
        {
          businessPlace: billingDetails?.businessPlace,
          costCenter: billingDetails?.costCenter,
          profitCenter: billingDetails?.profitCenter,
        },
        isNil
      )
      // Update Operation
      updateMutation({
        // variables: getVariables?.(state),
        variables: {
          ...baseVariables,
          certifications: changed?.licenseDetails
            ?.filter(b => !isEmpty(b))
            ?.map?.(licence => ({
              id: licence?.id,
              certificationTypeId: licence?.certificationType?.id,
              idNumber: licence?.idNumber,
              validFrom: licence?.validFrom,
              validTill:
                licence?.hasValidity === "yes"
                  ? licence?.validTill
                  : "1971-01-01",
              attachments: licence?.attachments?.map(p => ({
                id: p?.id,
                mediaType: "PHT",
                url: p?.url,
              })),
            })),
          bankDetails: changed?.paymentMethods
            ?.filter(b => !isEmpty(b))
            ?.map?.(method => ({
              id: Number(method?.id) || undefined,
              accountNumber: method?.accountNumber,
              accountHolderName: method?.accountHolderName || undefined,
              ifscCode: method?.ifscCode || undefined,
              bankId:
                method.paymentMethod?.id === Codenames.UPI
                  ? upi?.id
                  : method?.bank?.id,
              branchName: method?.branchName || undefined,
              attachments: method?.attachments?.map(p => ({
                id: p?.id,
                mediaType: "PHT",
                url: p?.url,
              })),
            })),
          id: store?.state?.basicDetails?.id,
          deliveryConditions: {
            deliveryCosts: deliveryConditions?.deliveryCosts
              ?.filter?.(c => c?.costRange?.[1])
              ?.map(c => omit(c, ["__typename"])),
            minCartValue: deliveryConditions?.minCartValue,
            isManufacturingPlant:
              typeof details?.isManufacturingPlant === "undefined"
                ? undefined
                : details?.isManufacturingPlant === "yes",
            estimatedDelta: deliveryConditions?.estimatedDelta?.id,
            extraSapCodes: isEmpty(extraSapCodes) ? undefined : extraSapCodes,
            gstDetails: isEmpty(gst)
              ? undefined
              : gst?.applicable && gst.applicable === "no"
              ? {}
              : {
                  ...omit(gst, ["__typename", "applicable", "state"]),
                  stateCode: gst?.state?.codename || gst?.state?.id,
                },
          },
          deliverySlots: slots?.deliverySlots?.map?.(slot =>
            mapSlots(slot, details?.plantCode, "delivery")
          ),
          pickupSlots: slots?.pickupSlots?.map?.(slot =>
            mapSlots(slot, details?.plantCode, "pickup")
          ),
          maintenanceSlots: slots?.maintenanceSlots?.map?.(slot =>
            mapSlots(slot, details?.plantCode, "maintenance")
          ),
          alternateSalesSlots: slots?.alternateSalesSlots?.map?.(slot =>
            mapSlots(slot, details?.plantCode, "alternateSalesSlots")
          ),
          surveyAreas: changed?.surveyAreas?.map(area => ({
            id: area.id.startsWith("vt")
              ? (undefined as any)
              : parseInt(area?.id),
            areaCatered: area?.areaCatered && {
              type: "Polygon",
              coordinates: [jsonToGis(area?.areaCatered)],
            },
            primaryUserId: area?.primaryUser?.id,
            name: area?.name,
            userIds: area?.users?.map(getId),
            codename: area?.codename,
            isActive: area?.isActive,
          })),
          ...((currentSP || currentSP === 0 || currentSP === "") && {
            currentStockPercentage: currentSP,
          }),
        },
      })
    } else {
      // Add Operation
      addMutation({
        variables: baseVariables,
      })
    }
    if (resetState) {
      navigate(ROUTES.dcDetail("add"), { replace: true })
    }
  }

  return { addUpdate, loading: addLoading || updateLoading || bLoading, data }
}
